import { useState, useEffect } from 'react';
import {
  USER_FEEDBACK_TYPES,
  USER_FEEDBACK_TYPE_LABELS,
  type UserFeedbackType,
} from '@safetyhub/shared';
import { Button, Modal } from '@safetyhub/ui';
import { captureScreenshot, collectDiagnostics } from '../../utils/diagnostics';
import { submitUserFeedback } from '../../lib/feedback';
import ScreenshotAnnotator from './ScreenshotAnnotator';
import ImageAttachment from './ImageAttachment';

type Step = 'capturing' | 'annotating' | 'form' | 'submitting' | 'done' | 'error';

/**
 * 제출 폼의 유형 선택지 — **shared 상수에서 파생**한다(2026-10-12 리뷰 ①). 여기 문구를 적지 말 것.
 *
 * ## ⚠️ 이 화면의 「개선 요청」은 **다른 값으로 옮겨졌다**
 * 종전 손나열은 선택지가 셋이었고 그중 **「개선 요청」이 `FEATURE_REQUEST`를 가리켰다** —
 * 즉 사용자가 고른 단어와 저장되는 값이 달랐고(운영자 화면·알림 메일은 같은 값을 「기능 요청」이라
 * 불렀다), 「새 기능을 만들어 달라」와 「있는 것을 고쳐 달라」가 **한 칸에 섞였다**.
 * 2026-10-12에 `IMPROVEMENT`를 늘려 축을 갈랐으므로 이제:
 *   - 「개선 요청」 → **`IMPROVEMENT`**(새 칸)
 *   - 「기능 요청」 → **`FEATURE_REQUEST`**(이 화면에 **새로 생긴 선택지**)
 * 선택지를 손으로 다시 적으면 그 매핑이 조용히 뒤집힌다 — 파생이 그것을 불가능하게 만든다.
 *
 * 섞인 과거 데이터(prod `FEATURE_REQUEST` 39건)를 어떻게 읽어야 하는지는
 * **shared `USER_FEEDBACK_TYPE_LABELS`·`UserFeedbackType` JSDoc이 정본**이다.
 */
const FEEDBACK_TYPES: { value: UserFeedbackType; label: string }[] = USER_FEEDBACK_TYPES.map(
  (value) => ({ value, label: USER_FEEDBACK_TYPE_LABELS[value] }),
);

export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialScreenshot?: Blob | null;
}

export default function FeedbackModal({ isOpen, onClose, initialScreenshot }: FeedbackModalProps) {
  const [step, setStep] = useState<Step>('capturing');
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  const [annotation, setAnnotation] = useState<Blob | null>(null);

  // 첨부 이미지 편집: 편집 중인 이미지 인덱스 (null이면 스크린샷 편집)
  const [editingImageIndex, setEditingImageIndex] = useState<number | null>(null);

  // 폼 상태
  // union으로 좁힌다 — `string`이면 오타가 그대로 서버로 가 400이 된다(`@IsEnum(UserFeedbackType)`).
  const [feedbackType, setFeedbackType] = useState<UserFeedbackType>('BUG_REPORT');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  // ObjectURL 관리: form 단계 진입 시점에만 생성, 언마운트/변경 시 해제
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (step !== 'form') return;
    const blob = annotation ?? screenshot;
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setPreviewUrl(null);
    };
  }, [step, annotation, screenshot]);

  // 모달이 열릴 때 초기화 및 스크린샷 준비
  useEffect(() => {
    if (!isOpen) return;
    setAnnotation(null);
    setEditingImageIndex(null);
    setFeedbackType('BUG_REPORT');
    setTitle('');
    setDescription('');
    setImages([]);
    setErrorMessage('');

    if (initialScreenshot) {
      // Layout에서 모달 열기 전 캡처한 스크린샷 사용 (모달 UI가 포함되지 않음)
      setScreenshot(initialScreenshot);
      setStep('annotating');
    } else {
      // 폴백: 모달 내부에서 캡처 (initialScreenshot이 없는 경우)
      setStep('capturing');
      setScreenshot(null);
      captureScreenshot().then((blob) => {
        setScreenshot(blob);
        setStep(blob ? 'annotating' : 'form');
      });
    }
  }, [isOpen, initialScreenshot]);

  // 스크린샷 메모 완료
  const handleAnnotationDone = (blob: Blob) => {
    setAnnotation(blob);
    setEditingImageIndex(null);
    setStep('form');
  };

  // 첨부 이미지 메모 완료: blob → File로 변환 후 images 배열 교체
  const handleImageAnnotationDone = (blob: Blob) => {
    if (editingImageIndex === null) return;
    const original = images[editingImageIndex];
    const annotatedFile = new File([blob], original.name, { type: 'image/jpeg' });
    setImages((prev) => prev.map((f, i) => (i === editingImageIndex ? annotatedFile : f)));
    setEditingImageIndex(null);
    setStep('form');
  };

  const handleAnnotationSkip = () => {
    setEditingImageIndex(null);
    setStep('form');
  };

  // 첨부 이미지 메모 편집 시작
  const handleEditImage = (index: number) => {
    setEditingImageIndex(index);
    setStep('annotating');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    setStep('submitting');
    try {
      const diagnostics = collectDiagnostics();
      // annotation이 있으면 annotation만 전송 (스크린샷 위에 그린 결과물이므로 screenshot 불필요)
      // annotation이 없으면 screenshot만 전송
      await submitUserFeedback({
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        diagnostics: JSON.stringify(diagnostics),
        pageUrl: window.location.href,
        screenshot: annotation ? undefined : (screenshot ?? undefined),
        annotation: annotation ?? undefined,
        images: images.length > 0 ? images : undefined,
      });
      setStep('done');
    } catch {
      setErrorMessage('피드백 제출에 실패했습니다. 잠시 후 다시 시도해 주세요.');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  // annotating 단계: ScreenshotAnnotator가 자체 portal로 전체 화면 표시
  // editingImageIndex !== null → 첨부 이미지 편집, null → 스크린샷 편집
  if (step === 'annotating') {
    if (editingImageIndex !== null && images[editingImageIndex]) {
      return (
        <ScreenshotAnnotator
          screenshotBlob={images[editingImageIndex]}
          onExport={handleImageAnnotationDone}
          onSkip={handleAnnotationSkip}
        />
      );
    }
    if (screenshot) {
      return (
        <ScreenshotAnnotator
          screenshotBlob={annotation ?? screenshot}
          onExport={handleAnnotationDone}
          onSkip={handleAnnotationSkip}
        />
      );
    }
  }

  // 전송 중에는 닫는 길(오버레이·Esc·✕) 전부를 막는다 — 종전에도 오버레이·✕가 `submitting`에서 꺼져 있었다.
  // 폭은 종전 `max-w-xl`(36rem)에 가장 가까운 base `lg`(32rem)로 스냅했다.
  // 버튼은 children의 `<form>` 안에 그대로 둔다(제출 Enter·`type="submit"`이 폼에 속해야 해서 —
  // base `footer`로 옮기면 폼 밖이 된다. `modalFooterSubmitFormGuard` 참조). 결과·오류 단계의 버튼도 본문 몫이다.
  const submitting = step === 'submitting';
  return (
    <Modal
      open
      onClose={onClose}
      title="피드백 보내기"
      size="lg"
      closeOnOverlay={!submitting}
      closeOnEsc={!submitting}
      showCloseButton={!submitting}
      closeLocked={submitting}
    >
            {/* 단계 표시 (form 단계만) — 종전엔 헤더 안 제목 아래였다(base 헤더는 제목 한 줄이라 본문 머리로 옮겼다) */}
            {step === 'form' && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-brand-600" />
                  <span className={`w-2 h-2 rounded-full ${screenshot ? 'bg-brand-600' : 'bg-ink-300'}`} />
                  <span className="w-2 h-2 rounded-full bg-brand-600" />
                </div>
                <span className="text-xs text-ink-400">내용 입력</span>
              </div>
            )}

          {/* 콘텐츠 */}
          <div>
            {/* 캡처 중 */}
            {step === 'capturing' && (
              <div className="flex flex-col items-center py-8 gap-3">
                <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-ink-500">화면을 캡처하는 중...</p>
              </div>
            )}

            {/* 폼 입력 */}
            {step === 'form' && (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* 스크린샷 미리보기 (주석 포함) — 클릭 시 메모 단계로 돌아가기 */}
                {previewUrl && screenshot && (
                  <div
                    className="relative rounded-card-sm overflow-hidden border border-line max-h-40 cursor-pointer group"
                    onClick={() => setStep('annotating')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setStep('annotating'); }}
                    aria-label="스크린샷 메모 수정"
                  >
                    <img
                      src={previewUrl}
                      alt="스크린샷 미리보기"
                      className="w-full object-contain max-h-40"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                      <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 px-3 py-1.5 rounded-full">
                        메모 수정
                      </span>
                    </div>
                  </div>
                )}
                {previewUrl && !screenshot && (
                  <div className="rounded-card-sm overflow-hidden border border-line max-h-40">
                    <img
                      src={previewUrl}
                      alt="스크린샷 미리보기"
                      className="w-full object-contain max-h-40"
                    />
                  </div>
                )}

                {/* 유형 선택 */}
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    피드백 유형
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {FEEDBACK_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setFeedbackType(t.value)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          feedbackType === t.value
                            ? 'border-brand-600 bg-brand-50 text-brand-700 font-medium'
                            : 'border-line text-ink-600 hover:bg-surface-hover'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 제목 */}
                <div>
                  <label htmlFor="fb-title" className="block text-sm font-medium text-ink-700 mb-1">
                    제목 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="fb-title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="문제나 요청 사항을 간략히 적어주세요"
                    maxLength={200}
                    className="w-full border border-line rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    required
                  />
                </div>

                {/* 내용 */}
                <div>
                  <label htmlFor="fb-description" className="block text-sm font-medium text-ink-700 mb-1">
                    내용 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="fb-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="어떤 문제가 발생했는지, 어떤 개선을 원하시는지 자세히 적어주세요"
                    rows={4}
                    maxLength={2000}
                    className="w-full border border-line rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                    required
                  />
                  <p className="text-xs text-ink-400 text-right mt-1">{description.length} / 2000</p>
                </div>

                {/* 추가 이미지 첨부 */}
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1">
                    추가 이미지 (선택)
                  </label>
                  <ImageAttachment files={images} onChange={setImages} onEditImage={handleEditImage} />
                </div>

                {/* 제출 버튼 */}
                <div className="flex justify-end gap-2 pt-2 border-t border-line-soft">
                  <Button variant="secondary" onClick={onClose}>
                    취소
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={!title.trim() || !description.trim()}
                  >
                    피드백 보내기
                  </Button>
                </div>
              </form>
            )}

            {/* 제출 중 */}
            {step === 'submitting' && (
              <div className="flex flex-col items-center py-8 gap-3">
                <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-ink-500">피드백을 전송하는 중...</p>
              </div>
            )}

            {/* 완료 */}
            {step === 'done' && (
              <div className="flex flex-col items-center py-8 gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-base font-semibold text-ink-900">감사합니다!</p>
                  <p className="text-sm text-ink-500 mt-1">피드백이 전달되었습니다. 서비스 개선에 활용하겠습니다.</p>
                </div>
                <Button variant="primary" onClick={onClose}>
                  닫기
                </Button>
              </div>
            )}

            {/* 오류 */}
            {step === 'error' && (
              <div className="flex flex-col items-center py-8 gap-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-base font-semibold text-ink-900">전송 실패</p>
                  <p className="text-sm text-ink-500 mt-1">{errorMessage}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setStep('form')}>
                    다시 시도
                  </Button>
                  <Button variant="primary" onClick={onClose}>
                    닫기
                  </Button>
                </div>
              </div>
            )}
          </div>
    </Modal>
  );
}
